import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, Hammer, Printer, UserCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/layout/PageHeader';
import { MetricCard } from '@/components/financials/MetricCard';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { Switch } from '@/components/ui/Switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import {
  PeopleSignoffList,
  SignoffMatrixGrid,
  StationSignoffList,
  type MatrixModel,
} from '@/components/production/EmployeeMatrixViews';
import { SignoffDialog } from '@/components/production/SignoffDialog';
import { SignoffSheet } from '@/components/production/SignoffSheet';
import { useAuth } from '@/app/AuthProvider';
import { canWrite } from '@/lib/permissions';
import { useIsMobile } from '@/lib/useMediaQuery';
import { formatDateOnly } from '@/lib/formatters';
import { printDocument } from '@/lib/printDocument';
import { useWorkLookups, type WorkLookups, type WorkPerson } from '@/features/workMode/useLookups';
import {
  matrixKey,
  signoffState,
  useStationInstructionHashes,
  useWorkstationSignoffs,
  type SignoffState,
} from '@/features/workMode/signoffs';
import type { StationInstructionHash, WorkstationSignoff } from '@/features/workMode/types';

const VIEWS = ['stations', 'matrix'] as const;
type View = (typeof VIEWS)[number];

function buildModel(
  lookups: WorkLookups,
  signoffs: WorkstationSignoff[],
  hashes: Map<string, StationInstructionHash> | undefined,
): MatrixModel {
  const people = lookups.peopleList.filter((p) => p.isActive && p.role !== 'external_lender');
  const peopleIds = new Set(people.map((p) => p.id));
  const states = new Map<string, SignoffState>();
  const active = new Map<string, WorkstationSignoff>();
  for (const s of signoffs) {
    states.set(s.id, signoffState(s, hashes));
    if (!s.revoked_at && s.workstation_id && peopleIds.has(s.user_id)) {
      active.set(matrixKey(s.workstation_id, s.user_id), s);
    }
  }
  const stationsWithSignoffs = new Set([...active.values()].map((s) => s.workstation_id));
  const stations = lookups.stationList.filter((s) => s.is_active || stationsWithSignoffs.has(s.id));
  const stationOrder = new Map(stations.map((s, i) => [s.id, i]));

  const byStation = new Map<string, WorkstationSignoff[]>();
  const byPerson = new Map<string, WorkstationSignoff[]>();
  for (const s of active.values()) {
    const stationId = s.workstation_id!;
    if (!stationOrder.has(stationId)) continue;
    byStation.set(stationId, [...(byStation.get(stationId) ?? []), s]);
    byPerson.set(s.user_id, [...(byPerson.get(s.user_id) ?? []), s]);
  }
  for (const rows of byStation.values()) {
    rows.sort((a, b) => lookups.personName(a.user_id).localeCompare(lookups.personName(b.user_id)));
  }
  for (const rows of byPerson.values()) {
    rows.sort((a, b) => stationOrder.get(a.workstation_id!)! - stationOrder.get(b.workstation_id!)!);
  }
  return { stations, people, active, byStation, byPerson, states, hashes };
}

function printMatrix(model: MatrixModel, people: WorkPerson[]) {
  const changed = [...model.active.values()].some((s) => model.states.get(s.id) === 'changed');
  const opened = printDocument({
    title: 'Employee matrix',
    subtitle: 'Workstation sign-offs',
    sections: [
      {
        table: {
          columns: [{ label: 'Employee' }, ...model.stations.map((s) => ({ label: s.name })), { label: 'Stations', align: 'right' as const }],
          rows: people.map((p) => [
            p.name,
            ...model.stations.map((s) => {
              const signoff = model.active.get(matrixKey(s.id, p.id));
              if (!signoff) return null;
              return `${formatDateOnly(signoff.signed_off_on)}${model.states.get(signoff.id) === 'changed' ? ' *' : ''}`;
            }),
            `${(model.byPerson.get(p.id) ?? []).length}/${model.stations.length}`,
          ]),
        },
      },
      ...(changed ? [{ text: '* The work instructions at that station have changed since the sign-off.' }] : []),
    ],
  });
  if (!opened) toast.error('Allow pop-ups to print or save the matrix as a PDF.');
}

/**
 * Manufacturing → Production → Employee matrix. The root lists every
 * workstation and who is signed off on it; the matrix tab lays the same
 * thing out as people × stations. Every sign-off opens its record with a
 * copy of the work instructions the person was signed off on.
 */
export function EmployeeMatrixPage() {
  const { role } = useAuth();
  const canManage = canWrite(role, 'manufacturing');
  const isMobile = useIsMobile();
  const lookups = useWorkLookups();
  const { data: signoffs = [], isLoading: signoffsLoading } = useWorkstationSignoffs();
  const { data: hashes } = useStationInstructionHashes();
  const [params, setParams] = useSearchParams();
  const view: View = (VIEWS as readonly string[]).includes(params.get('view') ?? '') ? (params.get('view') as View) : 'stations';
  const openId = params.get('signoff');
  const [grantFor, setGrantFor] = useState<{ stationId?: string | null; userId?: string | null } | null>(null);
  const [query, setQuery] = useState('');
  const [signedOnly, setSignedOnly] = useState(false);

  const model = useMemo(() => buildModel(lookups, signoffs, hashes), [lookups, signoffs, hashes]);

  const matrixPeople = useMemo(() => {
    const q = query.trim().toLowerCase();
    return model.people.filter(
      (p) =>
        (!signedOnly || (model.byPerson.get(p.id) ?? []).length > 0) &&
        (!q || p.name.toLowerCase().includes(q) || (p.jobTitle ?? '').toLowerCase().includes(q)),
    );
  }, [model, query, signedOnly]);

  const stats = useMemo(() => {
    const activeRows = [...model.active.values()].filter((s) => model.byStation.has(s.workstation_id!));
    return {
      stations: model.stations.length,
      uncovered: model.stations.filter((s) => !model.byStation.has(s.id)).length,
      people: model.byPerson.size,
      signoffs: activeRows.length,
      changed: activeRows.filter((s) => model.states.get(s.id) === 'changed').length,
    };
  }, [model]);

  const setParam = (key: string, value: string | null) => {
    const p = new URLSearchParams(params);
    if (value) p.set(key, value);
    else p.delete(key);
    setParams(p, { replace: key !== 'signoff' });
  };

  const openSignoff = (id: string | null) => setParam('signoff', id);
  const loading = lookups.loading || signoffsLoading;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Manufacturing / Production"
        title="Employee matrix"
        actions={
          <>
            <Button variant="outline" onClick={() => printMatrix(model, matrixPeople)} disabled={loading}>
              <Printer className="h-4 w-4" />
              <span className="hidden sm:inline">Print / PDF</span>
            </Button>
            {canManage && (
              <Button onClick={() => setGrantFor({})} disabled={loading}>
                <UserCheck className="h-4 w-4" />
                Sign off
              </Button>
            )}
          </>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard
          label="Workstations"
          value={String(stats.stations)}
          hint={stats.uncovered ? `${stats.uncovered} with nobody signed off` : stats.stations ? 'Every station covered' : undefined}
          loading={loading}
          icon={<Hammer className="h-4 w-4" />}
          tone={stats.uncovered ? 'negative' : 'neutral'}
        />
        <MetricCard
          label="People signed off"
          value={String(stats.people)}
          hint={`of ${model.people.length} active`}
          loading={loading}
          icon={<Users className="h-4 w-4" />}
        />
        <MetricCard
          label="Sign-offs"
          value={String(stats.signoffs)}
          loading={loading}
          icon={<UserCheck className="h-4 w-4" />}
          tone="positive"
        />
        <MetricCard
          label="Instructions changed"
          value={String(stats.changed)}
          hint={stats.changed ? 'Sign-offs to redo' : 'All sign-offs match the live instructions'}
          loading={loading}
          icon={<AlertTriangle className="h-4 w-4" />}
          tone={stats.changed ? 'negative' : 'neutral'}
        />
      </div>

      <Tabs value={view} onValueChange={(v) => setParam('view', v === 'stations' ? null : v)}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <TabsList className="h-auto self-start">
            <TabsTrigger value="stations">Workstations</TabsTrigger>
            <TabsTrigger value="matrix">Matrix</TabsTrigger>
          </TabsList>
          {view === 'matrix' && (
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search people" className="h-10 sm:w-64" />
              <label className="flex min-h-11 items-center gap-2 text-sm text-muted-foreground">
                <Switch checked={signedOnly} onCheckedChange={setSignedOnly} />
                Signed off only
              </label>
            </div>
          )}
        </div>

        <TabsContent value="stations">
          {loading ? (
            <Skeleton className="h-80 rounded-2xl" />
          ) : (
            <StationSignoffList
              model={model}
              lookups={lookups}
              canManage={canManage}
              onOpen={openSignoff}
              onSignOff={(stationId) => setGrantFor({ stationId })}
            />
          )}
        </TabsContent>
        <TabsContent value="matrix">
          {loading ? (
            <Skeleton className="h-80 rounded-2xl" />
          ) : isMobile ? (
            <PeopleSignoffList model={model} people={matrixPeople} onOpen={openSignoff} />
          ) : (
            <SignoffMatrixGrid
              model={model}
              people={matrixPeople}
              canManage={canManage}
              onOpen={openSignoff}
              onSignOff={(stationId, userId) => setGrantFor({ stationId, userId })}
            />
          )}
        </TabsContent>
      </Tabs>

      <SignoffSheet
        signoffId={openId}
        allSignoffs={signoffs}
        lookups={lookups}
        hashes={hashes}
        canManage={canManage}
        onOpenChange={(open) => !open && openSignoff(null)}
        onSelect={(id) => setParam('signoff', id)}
        onSignOffAgain={(stationId, userId) => setGrantFor({ stationId, userId })}
      />

      {grantFor && (
        <SignoffDialog
          stations={model.stations.filter((s) => s.is_active || s.id === grantFor.stationId)}
          people={model.people}
          active={model.active}
          initialStationId={grantFor.stationId}
          initialUserId={grantFor.userId}
          onClose={() => setGrantFor(null)}
          onGranted={(row) => setParams((prev) => {
            const p = new URLSearchParams(prev);
            p.set('signoff', row.id);
            return p;
          })}
        />
      )}
    </div>
  );
}
